import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, UserPlus, X, ArrowLeftRight, ChevronDown, Search, AlertTriangle } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { personMatch } from "../lib/searchText";

// Add a rider to a card from a dropdown + delete the card everywhere.
export const CardRiderActions = ({ card, employees, riderCards = {}, onChanged }) => {
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState("");
  const taken = new Set((card.riders || []).map((r) => r.id));
  const options = employees.filter((e) => !taken.has(e.id)).map((e) => ({ ...e, onCards: riderCards[e.id] || [] }));

  const add = async () => {
    if (!pick) return;
    const emp = options.find((o) => o.id === pick);
    const other = emp?.onCards?.[0];
    if (other && !window.confirm(`تنبيه: ${emp.name} مسجّل على الكرت ${emp.onCards.join("، ")}.\nسيُنقل إلى الكرت ${card.number} ويُزال من الكرت السابق. متابعة؟`)) return;
    setBusy("add");
    try {
      const r = other ? await api.moveOperatingCardRider(other, card.number, pick) : await api.addOperatingCardRider(card.number, pick);
      toast.success(other ? `تم نقل ${r.employee_name} من الكرت ${other} إلى ${card.number}` : `تمت إضافة ${r.employee_name} على الكرت ${card.number}`);
      setPick(""); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّرت الإضافة"); }
    finally { setBusy(""); }
  };

  const remove = async () => {
    if (!window.confirm(`حذف الكرت ${card.number} نهائياً؟ سيُحذف من ${card.count} مركبة ومن ملفات ${card.riders_count} مندوب.`)) return;
    setBusy("del");
    try {
      const r = await api.deleteOperatingCard(card.number);
      toast.success(`تم حذف الكرت — من ${r.vehicles_cleared} مركبة و${r.employees_cleared} مندوب`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
    finally { setBusy(""); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <RiderPicker testid={`opcard-add-rider-select-${card.number}`} options={options} value={pick} onChange={setPick} />
      <Button size="sm" variant="outline" data-testid={`opcard-add-rider-btn-${card.number}`} disabled={!pick || !!busy} onClick={add} className="h-8 gap-1">
        {busy === "add" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />} إضافة
      </Button>
      <Button size="sm" variant="ghost" data-testid={`opcard-delete-${card.number}`} disabled={!!busy} onClick={remove} className="h-8 gap-1 mr-auto text-rose-600 hover:bg-rose-50 hover:text-rose-700">
        {busy === "del" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} حذف الكرت
      </Button>
    </div>
  );
};

// Rider name chip with an × that detaches only this rider from the card.
export const RiderChip = ({ rider, card, cards = [], maxRiders = 3, onRemoved, dupCards = [] }) => {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!window.confirm(`إزالة ${rider.name} من الكرت ${card.number}؟ (يبقى الكرت لبقية المناديب)`)) return;
    setBusy(true);
    try {
      const r = await api.removeOperatingCardRider(card.number, rider.id);
      toast.success(`تمت إزالة ${rider.name}${r.vehicles_unlinked ? ` وفك ربط ${r.vehicles_unlinked} مركبة يستلمها` : ""}`);
      onRemoved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّرت الإزالة"); setBusy(false); }
  };
  return (
    <span data-testid={`opcard-rider-${card.number}-${rider.id}`}
      title={dupCards.length ? `مكرر — مسجّل أيضاً على الكرت ${dupCards.join("، ")}` : undefined}
      className={`inline-flex items-center gap-1 rounded-md border pr-2 pl-1 py-0.5 text-[11px] ${dupCards.length ? "bg-rose-50 border-rose-300 text-rose-700" : "bg-amber-50 border-amber-100 text-amber-800"}`}>
      {dupCards.length > 0 && <AlertTriangle data-testid={`opcard-dup-${card.number}-${rider.id}`} className="h-3 w-3 text-rose-600" />}
      {rider.name}
      {dupCards.length > 0 && <span className="text-[10px] font-semibold">(مكرر في {dupCards.join("، ")})</span>}
      <MoveRider rider={rider} card={card} cards={cards} maxRiders={maxRiders} onMoved={onRemoved} />
      <button type="button" data-testid={`opcard-remove-rider-${card.number}-${rider.id}`} disabled={busy} onClick={remove} title="إزالة من الكرت"
        className="grid h-4 w-4 place-items-center rounded-full text-amber-700 transition-colors hover:bg-rose-100 hover:text-rose-600">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
      </button>
    </span>
  );
};

// Popover listing cards that still have room; one click moves the rider there.
const MoveRider = ({ rider, card, cards, maxRiders, onMoved }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const targets = cards.filter((c) => c.number !== card.number && c.riders_count < (c.max_riders || maxRiders))
    .sort((a, b) => a.riders_count - b.riders_count);
  const move = async (to) => {
    setBusy(to);
    try {
      await api.moveOperatingCardRider(card.number, to, rider.id);
      toast.success(`تم نقل ${rider.name} إلى الكرت ${to}`);
      setOpen(false); onMoved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر النقل"); }
    finally { setBusy(""); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`opcard-move-rider-${card.number}-${rider.id}`} title="نقل إلى كرت آخر"
          className="grid h-4 w-4 place-items-center rounded-full text-amber-700 transition-colors hover:bg-sky-100 hover:text-sky-600">
          <ArrowLeftRight className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent dir="rtl" align="start" className="w-64 p-2" data-testid={`opcard-move-list-${rider.id}`}>
        <p className="px-1 pb-2 text-xs font-semibold text-slate-600">نقل {rider.name} إلى:</p>
        {targets.length === 0 ? <p className="px-1 py-2 text-xs text-slate-400">لا توجد كروت فيها مكان (الحدّ {maxRiders})</p> : (
          <div className="max-h-60 overflow-y-auto space-y-1">
            {targets.map((c) => (
              <button key={c.number} type="button" data-testid={`opcard-move-to-${c.number}`} disabled={!!busy} onClick={() => move(c.number)}
                className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-2 py-1.5 text-xs transition-colors hover:bg-emerald-50 hover:border-emerald-200">
                <span className="font-mono-nums font-semibold text-slate-700" dir="ltr">{c.number}</span>
                <span className="text-slate-400">{c.plate_number || ""}</span>
                {busy === c.number ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className="text-emerald-700">{(c.max_riders || maxRiders) - c.riders_count} مكان{c.vehicle_kind === "car" ? " · سيارة" : ""}</span>}
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const norm = (t) => String(t || "").toLowerCase().replace(/[أإآ]/g, "ا").replace(/ى/g, "ي").replace(/ة/g, "ه").replace(/\s+/g, " ").trim();

// Searchable rider dropdown (name / branch), Arabic-normalised.
const RiderPicker = ({ options, value, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const sel = options.find((o) => o.id === value);
  const nq = norm(q);
  const list = nq ? options.filter((o) => norm(`${o.name} ${o.branch || ""}`).includes(nq) || personMatch(o, q)) : options;
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(""); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid}
          className="h-8 w-56 inline-flex items-center justify-between gap-1 rounded-md border border-input bg-background px-2.5 text-xs text-right">
          <span className={`truncate ${sel ? "text-slate-800" : "text-muted-foreground"}`}>{sel ? sel.name : "إضافة مندوب على الكرت…"}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent dir="rtl" align="start" className="w-72 p-2">
        <div className="relative mb-2">
          <Search className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input autoFocus data-testid={`${testid}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو الفرع…" className="h-8 pr-8 text-xs" />
        </div>
        <div className="max-h-64 overflow-y-auto space-y-0.5">
          {list.length === 0 ? <p className="py-4 text-center text-xs text-slate-400">لا توجد نتائج</p> : list.slice(0, 200).map((o) => (
            <button key={o.id} type="button" data-testid={`${testid}-opt-${o.id}`} onClick={() => { onChange(o.id); setOpen(false); setQ(""); }}
              className={`w-full rounded-md px-2 py-1.5 text-right text-xs transition-colors hover:bg-amber-50 ${o.id === value ? "bg-amber-50 font-semibold" : ""}`}>
              {o.name}{o.branch ? <span className="text-slate-400"> — {o.branch}</span> : null}
              {o.onCards?.length > 0 && <span className="mr-1 rounded bg-rose-50 px-1 text-[10px] text-rose-600">على كرت {o.onCards.join("، ")}</span>}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
